import { FC, useState, useEffect } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  useToast,
  Text,
  Icon,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Spinner,
  Center,
  Box,
  Image
} from '@chakra-ui/react';
import { MdSave, MdArrowBack } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { useForm } from 'react-hook-form';
import { Panel, PageHeading } from '@bemap/ui';
import { getCourseById, updateCourse, Course } from '@/services/university-service';

interface CourseFormData {
  title: string;
  description: string;
  instructor: string;
  category: 'partner' | 'analyst';
  level: 'beginner' | 'intermediate' | 'advanced';
  duration: number;
  price: number;
  isActive: boolean;
  // maxStudents: number;
  // tags: string;
  // prerequisites: string;
  // learningObjectives: string;
}

const EditCoursePage: FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const toast = useToast();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState<Course | null>(null);
  // const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  // const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [certificateBgPreview, setCertificateBgPreview] = useState<string>('');
  const [selectedCertificateBgFile, setSelectedCertificateBgFile] = useState<File | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid }
  } = useForm<CourseFormData>({
    defaultValues: {
      title: '',
      description: '',
      instructor: '',
      category: 'partner',
      level: 'beginner',
      duration: 0,
      price: 0,
      isActive: true,
      // maxStudents: 100,
      // tags: '',
      // prerequisites: '',
      // learningObjectives: ''
    }
  });

  // Load course data
  useEffect(() => {
    const loadCourse = async () => {
      if (!courseId) return;

      try {
        setIsLoading(true);
        const response = await getCourseById(parseInt(courseId));

        if (response.statusCode === 200 && response.message) {
          const courseData = response.message as Course;
          setCourse(courseData);

          // Populate form with course data
          reset({
            title: courseData.title,
            description: courseData.description,
            instructor: courseData.instructor,
            category: courseData.category as 'partner' | 'analyst',
            level: courseData.level,
            duration: Math.floor(courseData.durationMinutes / 60),
            price: courseData.price || 0,
            isActive: courseData.active,
            // maxStudents: 100,
            // tags: '',
            // prerequisites: '',
            // learningObjectives: ''
          });

          // if (courseData.thumbnailUrl) {
          //   setThumbnailPreview(base64(courseData.thumbnailUrl));
          // }
          // Prefill certificate background preview if exists
          const bgUrl = (courseData as any).certificateBackgroundUrl as unknown as string;
          setCertificateBgPreview(bgUrl || '');
        } else {
          throw new Error(response.message || t('university.courses.course_not_found'));
        }
      } catch (error: any) {
        console.error('Error loading course:', error);
        toast({
          title: t('university.courses.error_loading_courses'),
          description: error.message || t('general.error'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
      } finally {
        setIsLoading(false);
      }
    };

    loadCourse();
  }, [courseId, navigate, toast, reset, t]);

  const handleBack = () => {
    navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
  };

  const onSubmit = async (data: CourseFormData) => {
    if (!courseId) return;

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('instructor', data.instructor);
      formData.append('category', data.category);
      formData.append('level', data.level);
      formData.append('active', data.isActive.toString());
      formData.append('durationMinutes', `${data.duration * 60}`);
      formData.append('price', `${data.price}`);
      if (selectedCertificateBgFile) {
        formData.append('certificateBackgroundUrl', selectedCertificateBgFile as File);
      }
      // formData.append('thumbnailUrl', selectedFile as File);

      const response = await updateCourse(parseInt(courseId), formData);

      if (response.statusCode === 200) {
        toast({
          title: t('university.courses.course_updated'),
          description: `${t('university.courses.edit_course')}${data.title}" ${t('university.courses.course_updated')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
      } else {
        throw new Error(response.message || t('university.courses.error_updating_course'));
      }
    } catch (error: any) {
      console.error('Error updating course:', error);
      toast({
        title: t('university.courses.error_updating_course'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     setSelectedFile(file);
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       const result = reader.result as string;
  //       setThumbnailPreview(result);
  //     };
  //     reader.readAsDataURL(file);
  //   }
  // };

  if (isLoading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>{t('general.loading')}</Text>
        </VStack>
      </Center>
    );
  }

  if (!course) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Text>{t('university.courses.course_not_found')}</Text>
          <Button onClick={handleBack}>{t('university.courses.back_to_courses')}</Button>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={`${t('university.courses.edit_course')}: ${course.title}`} actions={[
        {
          label: t('university.courses.back_to_courses'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        }
      ]} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack align="stretch" spacing={6}>
          {/* Basic Information */}
          <Panel heading={t('university.courses.basic_information')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Status</FormLabel>
                <HStack>
                  <Switch {...register('isActive')} colorScheme="blue" />
                  <Text fontSize="sm" color="gray.600">
                    {watch('isActive') ? t('university.courses.status.active') : t('university.courses.status.draft')}
                  </Text>
                </HStack>
              </FormControl>
              <FormControl isInvalid={!!errors.title} isRequired>
                <FormLabel>{t('university.courses.course_title')}</FormLabel>
                <Input
                  {...register('title', {
                    required: t('university.validation.title_required'),
                    minLength: { value: 3, message: t('university.validation.title_min_length') }
                  })}
                  placeholder={t('university.placeholders.course_title')}
                />
                <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.description} isRequired>
                <FormLabel>{t('university.courses.description')}</FormLabel>
                <Textarea
                  {...register('description', {
                    required: t('university.validation.description_required'),
                    minLength: { value: 10, message: t('university.validation.description_min_length') }
                  })}
                  placeholder={t('university.placeholders.course_description')}
                  rows={4}
                />
                <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
              </FormControl>

              <HStack spacing={4}>
                <FormControl isInvalid={!!errors.instructor} isRequired>
                  <FormLabel>{t('university.courses.instructor')}</FormLabel>
                  <Input
                    {...register('instructor', {
                      required: t('university.validation.instructor_required')
                    })}
                    placeholder={t('university.placeholders.instructor_name')}
                  />
                  <FormErrorMessage>{errors.instructor?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.category} isRequired>
                  <FormLabel>{t('university.courses.category')}</FormLabel>
                  <Select {...register('category', {
                    required: t('university.validation.category_required')
                  })}>
                    <option value="partner">{t('university.courses.categories.partner')}</option>
                    <option value="analyst">{t('university.courses.categories.analyst')}</option>
                  </Select>
                  <FormErrorMessage>{errors.category?.message}</FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>{t('university.courses.level')}</FormLabel>
                  <Select {...register('level')}>
                    <option value="beginner">{t('university.levels.0.beginner')}</option>
                    <option value="intermediate">{t('university.levels.1.intermediate')}</option>
                    <option value="advanced">{t('university.levels.2.advanced')}</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.courses.duration_hours')}</FormLabel>
                  <NumberInput min={0}>
                    <NumberInputField {...register('duration', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.courses.price')}</FormLabel>
                  <NumberInput min={0}>
                    <NumberInputField {...register('price', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>
            </VStack>
          </Panel>

          {/* Certificate Background Image */}
          <Panel heading={t('university.courses.certificate.panel')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('university.courses.certificate.background_image')}</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedCertificateBgFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const result = reader.result as string;
                        setCertificateBgPreview(result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  p={1}
                />
              </FormControl>

              {certificateBgPreview && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {t('university.courses.preview')}
                  </Text>
                  <Image
                    src={certificateBgPreview}
                    alt="Certificate background preview"
                    maxH="200px"
                    borderRadius="md"
                    objectFit="cover"
                  />
                </Box>
              )}
            </VStack>
          </Panel>

          {/* Course Image */}
          {/* <Panel heading={t('university.courses.course_thumbnail')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('university.courses.upload_thumbnail')}</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  p={1}
                />
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {t('university.courses.recommended_size')}
                </Text>
              </FormControl>

              {thumbnailPreview && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {t('university.courses.preview')}
                  </Text>
                  <Image
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    maxH="200px"
                    borderRadius="md"
                    objectFit="cover"
                  />
                </Box>
              )}
            </VStack>
          </Panel> */}

          {/* Additional Details */}
          {/* <Panel heading={t('university.courses.additional_details')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('university.courses.learning_objectives')}</FormLabel>
                <Textarea
                  {...register('learningObjectives')}
                  placeholder={t('university.placeholders.learning_objectives')}
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('university.courses.prerequisites')}</FormLabel>
                <Textarea
                  {...register('prerequisites')}
                  placeholder={t('university.placeholders.prerequisites')}
                  rows={2}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('university.courses.tags')}</FormLabel>
                <Input
                  {...register('tags')}
                  placeholder={t('university.placeholders.tags_example')}
                />
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>{t('university.courses.max_students')}</FormLabel>
                  <NumberInput min={1} max={1000}>
                    <NumberInputField {...register('maxStudents', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>
            </VStack>
          </Panel> */}

          {/* Action Buttons */}
          <HStack justify="space-between">
            <Button
              variant="outline"
              onClick={handleBack}
              isDisabled={isSubmitting}
            >
              {t('actions.cancel')}
            </Button>

            <HStack spacing={3}>
              {/* <Button
                  variant="outline"
                  leftIcon={<Icon as={MdPreview} />}
                  isDisabled={!isValid || isSubmitting}
                >
                  Preview
                </Button> */}

              <Button
                type="submit"
                colorScheme="blue"
                leftIcon={<Icon as={MdSave} />}
                isLoading={isSubmitting}
                loadingText={t('general.saving')}
                isDisabled={!isValid}
              >
                {t('actions.save')}
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};

export default EditCoursePage; 